import { Battery, Zap, Clock, MapPin, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import type { Site } from '../types';

interface SiteCardProps {
  site: Site;
}

export function SiteCard({ site }: SiteCardProps) {
  const navigate = useNavigate();
  const lastOptTime = site.last_optimization?.created_at
    ? formatDistanceToNow(new Date(site.last_optimization.created_at), { addSuffix: true })
    : 'Never';

  return (
    <div
      onClick={() => navigate(`/site/${site.id}`)}
      className={`bg-card dark:bg-brand-navy rounded-lg border ${
        site.active
          ? 'border-brand-orange/30'
          : 'border-gray-200 dark:border-gray-700'
      } p-4 hover:shadow-md hover:border-brand-orange dark:hover:shadow-brand-orange/10 transition-all cursor-pointer group`}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-brand-orange transition-colors">
            {site.name}
          </h3>
          <div className="flex items-center gap-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
            <MapPin className="w-3 h-3" />
            <span>{site.region}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 text-xs font-medium rounded-full ${
              site.active
                ? 'bg-brand-orange/10 text-brand-orange dark:bg-brand-orange/20'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {site.active ? 'Active' : 'Inactive'}
          </span>
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-brand-orange transition-colors" />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Battery className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Capacity</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{site.battery.capacity_kwh} kWh</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-gray-400 dark:text-gray-500" />
          <div>
            <p className="text-xs text-gray-500 dark:text-gray-400">Max Power</p>
            <p className="text-sm font-medium text-gray-900 dark:text-white">{site.battery.max_charge_kw} kW</p>
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
            <Clock className="w-3 h-3" />
            <span>Last optimization</span>
          </div>
          <span className="text-gray-700 dark:text-gray-300">{lastOptTime}</span>
        </div>
        {site.last_optimization && (
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            Trigger: {site.last_optimization.trigger}
          </p>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1">
        <span className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded">
          {site.providers.price}
        </span>
        <span className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded">
          {site.providers.optimizer}
        </span>
        <span className="px-2 py-0.5 text-xs bg-orange-50 text-orange-700 dark:bg-orange-900 dark:text-orange-300 rounded">
          {site.providers.battery}
        </span>
      </div>
    </div>
  );
}
